import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { ArrowLeft, Camera, UserCircle2 } from "lucide-react";

export default function FPBeneficiaryForm() {
  const nav = useNavigate();
  const { id } = useParams();
  const editing = !!id;
  const [form, setForm] = useState({
    name: "", birth_date: "", gender: "", cpf: "", phone: "",
    address: "", cep: "", city: "", state: "",
    emergency_contact: { name: "", phone: "", relation: "" },
    relation_to_owner: "", photo_url: "", notes: "",
    consent_lgpd: false, consent_sensitive_data: false,
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(editing);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!editing) {
      toast.info("Salve o cadastro primeiro para adicionar a foto.");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/fp/beneficiaries/${id}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("photo_url", data.url);
      toast.success("Foto atualizada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro no upload");
    } finally { setUploading(false); e.target.value = ""; }
  };

  useEffect(() => {
    if (!editing) return;
    (async () => {
      try {
        const { data } = await api.get(`/fp/beneficiaries/${id}`);
        setForm({ ...form, ...data, emergency_contact: data.emergency_contact || { name: "", phone: "", relation: "" }, consent_lgpd: true, consent_sensitive_data: true });
      } catch { toast.error("Não encontrado"); nav("/filho-presente/familiares"); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setEmergency = (k, v) => setForm((f) => ({ ...f, emergency_contact: { ...f.emergency_contact, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    if (!editing && !(form.consent_lgpd && form.consent_sensitive_data)) {
      toast.error("Aceite os termos antes de salvar."); return;
    }
    setSaving(true);
    try {
      if (editing) {
        const patch = { ...form };
        delete patch.consent_lgpd; delete patch.consent_sensitive_data;
        delete patch.owner_id; delete patch.consent_snapshot; delete patch.id;
        await api.patch(`/fp/beneficiaries/${id}`, patch);
        toast.success("Familiar atualizado");
      } else {
        const { data } = await api.post("/fp/beneficiaries", form);
        toast.success("Familiar cadastrado com sucesso");
        nav(`/filho-presente/familiares/${data.id}`);
        return;
      }
      nav("/filho-presente/familiares");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-10 text-slate-500">Carregando…</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="fp-beneficiary-form">
      <Link to="/filho-presente/familiares" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0B2B62] mb-4">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </Link>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0B2B62] mb-2">
        {editing ? "Editar familiar" : "Cadastrar familiar"}
      </h1>
      <p className="text-slate-600 text-sm mb-6">Dados do beneficiário sob sua responsabilidade.</p>

      <form onSubmit={submit} className="space-y-6 bg-white border border-slate-200 rounded-xl p-6">
        {/* Foto do beneficiário */}
        <section className="flex items-center gap-4 pb-4 border-b border-slate-100">
          <div className="relative">
            {form.photo_url ? (
              <img src={form.photo_url} alt="" data-testid="fp-beneficiary-photo"
                className="w-20 h-20 rounded-full object-cover border-2 border-slate-200" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center border-2 border-slate-200">
                <UserCircle2 className="w-10 h-10 text-slate-400" />
              </div>
            )}
            <button type="button" onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="fp-beneficiary-photo-btn"
              className="absolute -bottom-1 -right-1 bg-[#0B2B62] text-white p-2 rounded-full hover:bg-slate-800 disabled:opacity-60">
              <Camera className="w-3.5 h-3.5" />
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadPhoto}
              data-testid="fp-beneficiary-photo-input" />
          </div>
          <div className="text-xs text-slate-500">
            {uploading
              ? "Enviando foto…"
              : editing
                ? "Adicione ou troque a foto do familiar. Isso ajuda o consultor e os profissionais a reconhecerem quem estão cuidando."
                : "Salve o cadastro primeiro; depois você poderá adicionar a foto do familiar."}
          </div>
        </section>

        <section>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">Identificação</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input label="Nome completo *" value={form.name} onChange={(v) => set("name", v)} testId="fp-input-name" required />
            <Input label="Data de nascimento *" type="date" value={form.birth_date} onChange={(v) => set("birth_date", v)} testId="fp-input-birth" required />
            <Select label="Sexo" value={form.gender} onChange={(v) => set("gender", v)} options={["", "Feminino", "Masculino", "Outro", "Prefiro não informar"]} testId="fp-input-gender" />
            <Input label="CPF" value={form.cpf} onChange={(v) => set("cpf", v)} testId="fp-input-cpf" />
            <Input label="Telefone" value={form.phone} onChange={(v) => set("phone", v)} testId="fp-input-phone" />
            <Input label="Grau de parentesco (com você)" value={form.relation_to_owner} onChange={(v) => set("relation_to_owner", v)} placeholder="Mãe, pai, avó..." testId="fp-input-relation" />
          </div>
        </section>

        <section>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">Endereço</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input label="CEP" value={form.cep} onChange={(v) => set("cep", v)} testId="fp-input-cep" />
            <Input label="Cidade" value={form.city} onChange={(v) => set("city", v)} testId="fp-input-city" />
            <Input label="Estado (UF)" value={form.state} onChange={(v) => set("state", v)} maxLength={2} testId="fp-input-state" />
            <Input label="Endereço completo" value={form.address} onChange={(v) => set("address", v)} testId="fp-input-address" />
          </div>
        </section>

        <section>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">Contato de emergência</div>
          <div className="grid sm:grid-cols-3 gap-4">
            <Input label="Nome" value={form.emergency_contact.name} onChange={(v) => setEmergency("name", v)} testId="fp-input-emerg-name" />
            <Input label="Telefone" value={form.emergency_contact.phone} onChange={(v) => setEmergency("phone", v)} testId="fp-input-emerg-phone" />
            <Input label="Parentesco" value={form.emergency_contact.relation} onChange={(v) => setEmergency("relation", v)} testId="fp-input-emerg-relation" />
          </div>
        </section>

        <section>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">Observações importantes</div>
          <textarea rows={3} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)}
            data-testid="fp-input-notes"
            placeholder="Alergias, condições, informações relevantes ao cuidado (opcional)"
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
        </section>

        {!editing && (
          <section className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2">
            <div className="text-xs uppercase tracking-widest text-amber-800 font-bold">Consentimentos (LGPD)</div>
            <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" checked={form.consent_lgpd} onChange={(e) => set("consent_lgpd", e.target.checked)}
                data-testid="fp-consent-lgpd" className="mt-1" />
              <span>Aceito os <Link to="/termos-de-uso" className="underline">Termos de Uso</Link> e a <Link to="/politica-de-privacidade" className="underline">Política de Privacidade</Link> do RAF Direct.</span>
            </label>
            <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" checked={form.consent_sensitive_data} onChange={(e) => set("consent_sensitive_data", e.target.checked)}
                data-testid="fp-consent-sensitive" className="mt-1" />
              <span>Autorizo o tratamento de dados sensíveis de saúde do beneficiário para coordenação de cuidados (Art. 11 LGPD).</span>
            </label>
          </section>
        )}

        <div className="flex gap-3 justify-end">
          <button type="button" onClick={() => nav(-1)} className="px-4 py-2 text-sm text-slate-600 hover:text-[#0B2B62]">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="fp-submit-beneficiary"
            className="px-6 py-2.5 bg-[#0B2B62] text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 text-sm font-medium">
            {saving ? "Salvando…" : editing ? "Salvar alterações" : "Cadastrar familiar"}
          </button>
        </div>
      </form>
    </div>
  );
}

const Input = ({ label, testId, onChange, ...rest }) => (
  <label className="block text-sm">
    <div className="text-slate-700 mb-1 font-medium">{label}</div>
    <input {...rest} onChange={(e) => onChange && onChange(e.target.value)} data-testid={testId}
      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0B2B62]/20" />
  </label>
);

const Select = ({ label, value, onChange, options, testId }) => (
  <label className="block text-sm">
    <div className="text-slate-700 mb-1 font-medium">{label}</div>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm">
      {options.map((o) => <option key={o} value={o}>{o || "Selecione..."}</option>)}
    </select>
  </label>
);
